/**
 * GIWAHS — Supplier RPC client
 * Thin wrapper around the SECURITY DEFINER RPCs added by migration 007.
 * Authorization is server-side (is_supplier / my_supplier_id checks).
 */
import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import { dispatch } from './notifications';
import { track, EVENTS } from './analytics';

const sb = () => {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured.');
  return getSupabaseClient();
};

export const supplierClient = {
  async isSupplier() {
    try {
      const { data, error } = await sb().rpc('is_supplier');
      if (error) throw error;
      return Boolean(data);
    } catch { return false; }
  },

  async mySupplierId() {
    const { data, error } = await sb().rpc('my_supplier_id');
    if (error) throw error;
    return data || null;
  },

  async claimListing({ supplier_slug, contact_email, contact_role, proof_url, reason }) {
    const { data, error } = await sb().rpc('supplier_claim_listing', {
      p_supplier_slug: supplier_slug,
      p_contact_email: contact_email,
      p_contact_role: contact_role || null,
      p_proof_url: proof_url || null,
      p_reason: reason || null,
    });
    if (error) throw error;
    track(EVENTS.SUPPLIER_CLAIM_SUBMITTED, { supplier_slug });
    dispatch({
      kind: 'supplier_claim_received',
      to: null, // admin-routed
      payload: {
        supplier_slug,
        contact_email,
        contact_role: contact_role || '',
        proof_url: proof_url || '',
        reason: reason || '',
      },
    });
    return data;
  },

  async myProfile() {
    const { data, error } = await sb().rpc('supplier_my_profile');
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  },

  async updateProfile(payload) {
    const { error } = await sb().rpc('supplier_update_profile', { payload });
    if (error) throw error;
    track(EVENTS.SUPPLIER_PROFILE_UPDATED, {});
    return { ok: true };
  },

  async myRfqs() {
    const { data, error } = await sb().rpc('supplier_my_rfqs');
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  },

  async getRfq(reference) {
    const { data, error } = await sb().rpc('supplier_get_rfq', { p_ref: reference });
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  },

  async submitQuote(reference, q) {
    const { data, error } = await sb().rpc('supplier_submit_quote', {
      p_ref: reference,
      p_unit_price: q.unit_price,
      p_moq: q.moq ?? null,
      p_lead_time_days: q.lead_time_days ?? null,
      p_notes: q.notes ?? null,
      p_attachments_url: q.attachments_url ?? null,
    });
    if (error) throw error;
    track(EVENTS.SUPPLIER_QUOTE_SUBMITTED, { reference_code: reference });

    // Fire buyer + admin notifications — best-effort, non-blocking.
    try {
      const [rfqRow, me] = await Promise.all([
        sb().rpc('supplier_get_rfq', { p_ref: reference }),
        this.myProfile().catch(() => null),
      ]);
      const rfq = Array.isArray(rfqRow?.data) ? rfqRow.data[0] : rfqRow?.data;
      const supplier_slug = me?.slug || '';
      const supplier_name = me?.name || supplier_slug;
      const basePayload = {
        reference_code: reference,
        supplier_slug,
        supplier_name,
        unit_price: q.unit_price,
        moq: q.moq ?? null,
        lead_time_days: q.lead_time_days ?? null,
        notes: q.notes ?? '',
        attachments_url: q.attachments_url ?? '',
      };
      // Buyer
      if (rfq?.buyer_email) {
        dispatch({
          kind: 'quote_submitted_buyer',
          to: rfq.buyer_email,
          payload: {
            ...basePayload,
            contact_name: rfq.buyer_name || '',
            category: (rfq.product_type || '').split(':')[0]?.trim() || rfq.product_type,
            product_name: (rfq.product_type || '').split(':')[1]?.trim() || '',
            quantity: rfq.target_quantity,
            destination_country: rfq.shipping_destination,
          },
        });
      }
      // Admin
      dispatch({ kind: 'quote_submitted_admin_alert', to: null, payload: basePayload });
    } catch (e) {
      console.warn('[supplierClient] quote-submitted notify skipped:', e?.message);
    }

    return data;
  },

  async metrics() {
    const { data, error } = await sb().rpc('supplier_metrics');
    if (error) throw error;
    return data || {};
  },

  // ── Verification (P4) ─────────────────────────────────────────────────────
  async myVerification(supplier_slug) {
    const { data, error } = await sb().rpc('supplier_my_verification', {
      p_supplier_slug: supplier_slug,
    });
    if (error) throw error;
    return data || {};
  },

  async submitVerification({ supplier_slug, requested_tier, attestation }) {
    const { data, error } = await sb().rpc('supplier_submit_verification', {
      p_supplier_slug: supplier_slug,
      p_requested_tier: requested_tier,
      p_attestation: attestation || null,
    });
    if (error) throw error;
    track(EVENTS.SUPPLIER_VERIFICATION_SUBMITTED, { supplier_slug, requested_tier });
    dispatch({
      kind: 'verification_submitted',
      to: null,
      payload: { supplier_slug, requested_tier, attestation: attestation || '' },
    });
    return data;
  },

  async uploadDocument({ supplier_slug, request_id, doc_type, file }) {
    if (!file) throw new Error('No file selected.');
    if (file.size > 25 * 1024 * 1024) throw new Error('File exceeds 25 MB limit.');
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!allowed.includes(file.type)) throw new Error('Unsupported file type. Use PDF / JPG / PNG / WEBP / HEIC.');

    const client = sb();
    const { data: { session } } = await client.auth.getSession();
    if (!session?.user?.id) throw new Error('Sign-in required.');
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${supplier_slug}/${doc_type}/${Date.now()}_${safeName}`;

    const { error: upErr } = await client.storage.from('supplier-docs').upload(path, file, { upsert: false });
    if (upErr) throw upErr;

    const { data: docId, error: regErr } = await client.rpc('supplier_register_document', {
      p_supplier_slug: supplier_slug,
      p_request_id:    request_id,
      p_doc_type:      doc_type,
      p_storage_path:  path,
      p_file_name:     file.name,
    });
    if (regErr) throw regErr;
    track(EVENTS.SUPPLIER_DOC_UPLOADED, { supplier_slug, doc_type });
    return { ok: true, doc_id: docId, storage_path: path };
  },
};
